// Command/query request schemas from docs/10-service-contracts.md, as zod
// schemas — the parsed/validated shape IS the TS request type via z.infer.
import { z } from "zod";
import { EVENT_TYPES, PALETTE, QUESTION_TYPES, RELATIONSHIP_TYPES } from "./enums.js";
import { CONSENT_BASES } from "./consent.js";
const id = z.string().min(1);
const title = z.string().trim().min(1);
const color = z.enum(PALETTE);
const dateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const layoutFields = {
    x: z.number(),
    y: z.number(),
    width: z.number().positive(),
    height: z.number().positive(),
    zIndex: z.number(),
    color,
    collapsed: z.boolean(),
};
const relationshipLayoutFields = {
    curve: z.string(),
    labelX: z.number().nullable(),
    labelY: z.number().nullable(),
    stroke: z.string(),
    zIndex: z.number(),
};
const answerOptionInput = z.object({
    sortOrder: z.number().positive(),
    text: z.string(),
    isCorrect: z.boolean(),
});
const rubricItemInput = z.object({
    sortOrder: z.number().positive(),
    text: z.string(),
    points: z.number().positive(),
});
const questionInput = z.object({
    questionNumber: z.number().int().positive(),
    type: z.enum(QUESTION_TYPES),
    prompt: z.string().trim().min(1),
    correctAnswer: z.string().nullable().optional(),
    explanation: z.string().trim().min(1),
    answerOptions: z.array(answerOptionInput),
    rubricItems: z.array(rubricItemInput),
});
// ---- Subjects ----
export const createSubjectSchema = z.object({ title, color });
export const updateSubjectSchema = z.object({ subjectId: id, title, color });
export const subjectIdSchema = z.object({ subjectId: id });
// ---- Chapters ----
export const createChapterSchema = z.object({ subjectId: id, title });
export const updateChapterSchema = z.object({ chapterId: id, title });
export const reorderChaptersSchema = z.object({ subjectId: id, chapterIds: z.array(id) });
export const chapterIdSchema = z.object({ chapterId: id });
// ---- Check items ----
// subjectId omitted/null => a subject-less ("personal") task owned by the user.
export const createCheckItemSchema = z.object({ subjectId: id.nullable().optional(), text: z.string().trim().min(1) });
export const updateCheckItemSchema = z.object({
    checkItemId: id,
    text: z.string().trim().min(1).optional(),
    done: z.boolean().optional(),
});
export const reorderCheckItemsSchema = z.object({ subjectId: id, checkItemIds: z.array(id) });
export const checkItemIdSchema = z.object({ checkItemId: id });
// ---- Events ----
const eventFields = {
    title: z.string().trim().min(1),
    type: z.enum(EVENT_TYPES),
    startDate: dateOnly,
    endDate: dateOnly.nullable().optional(),
};
// subjectId omitted/null => a subject-less ("personal") event owned by the user.
export const createEventSchema = z.object({ subjectId: id.nullable().optional(), ...eventFields });
export const updateEventSchema = z.object({ eventId: id, ...eventFields });
export const eventIdSchema = z.object({ eventId: id });
// ---- Mind maps ----
// Every subject owns exactly one subject-level map and every chapter exactly
// one chapter map. createMindMap exists for lazy creation of that single map
// (the server rejects a second map for the same scope with invalidState);
// there is no rename/reorder/delete — maps live and die with their scope.
export const createMindMapSchema = z.object({ subjectId: id, chapterId: id.nullable().optional(), title });
export const mindMapIdSchema = z.object({ mindMapId: id });
// ---- Nodes / writings / relationships ----
export const createNodeSchema = z.object({
    mindMapId: id,
    title: z.string(),
    markdown: z.string().optional(),
    connectFromNodeId: id.nullable().optional(),
    ...layoutFields,
});
export const updateNodeSchema = z.object({ nodeId: id, title: z.string(), ...layoutFields });
export const updateWritingSchema = z.object({ nodeId: id, markdown: z.string() });
export const updateMindMapWritingSchema = z.object({ mindMapId: id, markdown: z.string() });
export const nodeIdSchema = z.object({ nodeId: id });
export const createNodeRelationshipSchema = z.object({
    mindMapId: id,
    fromNodeId: id,
    toNodeId: id,
    type: z.enum(RELATIONSHIP_TYPES),
    label: z.string(),
    ...relationshipLayoutFields,
});
export const updateNodeRelationshipSchema = z.object({
    relationshipId: id,
    type: z.enum(RELATIONSHIP_TYPES),
    label: z.string(),
    ...relationshipLayoutFields,
});
export const relationshipIdSchema = z.object({ relationshipId: id });
// ---- AI tests ----
export const generateAITestSchema = z.object({
    subjectId: id,
    /** Deprecated single-scope input retained for older app versions. */
    chapterId: id.nullable().optional(),
    chapterIds: z.array(id).default([]),
    includeSubjectNotes: z.boolean().optional(),
    title: z.string().trim().min(1).nullable().optional(),
    questionCount: z.number().int().positive().max(20),
    allowedQuestionTypes: z.array(z.enum(QUESTION_TYPES)).min(1),
});
// Tests are AI-generated only — there is no manual-creation command. The
// questions of an existing (unattempted) test can still be edited via
// replaceAITestQuestions.
export const updateAITestSchema = z.object({ testId: id, title, chapterId: id.nullable().optional() });
export const replaceAITestQuestionsSchema = z.object({ testId: id, questions: z.array(questionInput) });
export const reorderAITestsSchema = z.object({ subjectId: id, testIds: z.array(id) });
export const testIdSchema = z.object({ testId: id });
// ---- Attempts ----
export const saveQuestionResponseSchema = z.object({
    attemptId: id,
    questionId: id,
    selectedAnswerOptionIds: z.array(id).optional(),
    answerText: z.string().nullable().optional(),
});
export const attemptIdSchema = z.object({ attemptId: id });
export const responseIdSchema = z.object({ responseId: id });
export const markFreeTextResponseSchema = z.object({ responseId: id }).strict();
// ---- Read queries ----
export const noArgsSchema = z.object({});
export const listCreditTransactionsSchema = z.object({
    offset: z.coerce.number().int().nonnegative().default(0),
    limit: z.coerce.number().int().min(1).max(100).default(25),
});
export const grantCreditTopUpSchema = z.object({
    userId: id,
    amount: z.number().int().positive().max(100_000),
    reason: z.string().trim().min(5).max(500),
    requestId: z.string().uuid(),
}).strict();
export const TRANSLATION_LOCALES = ["af", "nr", "nso", "ss", "st", "tn", "ts", "ve", "xh", "zu"];
export const translationLocaleSchema = z.enum(TRANSLATION_LOCALES);
export const listTranslationWorkSchema = z.object({
    locale: translationLocaleSchema,
    offset: z.coerce.number().int().nonnegative().default(0),
}).strict();
export const translationOverridesSchema = z.object({ locale: translationLocaleSchema }).strict();
export const submitTranslationSuggestionSchema = z.object({
    locale: translationLocaleSchema,
    messageKey: z.string().min(1).max(255),
    targetHash: z.string().length(64),
    candidate: z.string().trim().min(1).max(300),
    reason: z.string().trim().min(10).max(500),
    requestId: z.string().uuid(),
}).strict();
export const submitTranslationReviewSchema = z.object({
    locale: translationLocaleSchema,
    messageKey: z.string().min(1).max(255),
    targetHash: z.string().length(64),
    suggestionId: id.optional(),
    verdict: z.enum(["approve", "reject", "unsure"]),
}).strict();
export const setTranslationCampaignSchema = z.object({
    locale: translationLocaleSchema,
    enabled: z.boolean(),
    reason: z.string().trim().min(10).max(500),
}).strict();
export const disableTranslationOverrideSchema = z.object({
    locale: translationLocaleSchema,
    messageKey: z.string().min(1).max(255),
    reason: z.string().trim().min(10).max(500),
}).strict();
export const eventsByDateRangeSchema = z.object({
    subjectId: id,
    from: dateOnly.optional(),
    to: dateOnly.optional(),
    type: z.enum(EVENT_TYPES).optional(),
});
export const upcomingEventsSchema = z.object({
    from: dateOnly.optional(),
    to: dateOnly.optional(),
    type: z.enum(EVENT_TYPES).optional(),
});
export const recentMindMapsSchema = z.object({ limit: z.coerce.number().int().positive().optional() });
// ---- User settings ----
// Every field optional: this is a PATCH, so a caller changing only the theme
// must not have to resend (and risk clobbering) the others. `null` is
// meaningful and distinct from absent — it clears a setting back to
// "not chosen", letting the client fall back to its own default again.
export const updatePreferencesSchema = z.object({
    language: z.string().min(2).max(16).nullish(),
    theme: z.enum(["light", "dark"]).nullish(),
    gridSize: z.number().int().positive().max(500).nullish(),
    exploredWithoutSubject: z.boolean().optional(),
});
// ---- Consent ----
// The client sends the version it showed the user, not the server's idea of
// the current one: if the documents changed between the screen rendering and
// the click, the server refuses rather than record agreement to text the user
// never saw.
export const acceptConsentSchema = z.object({
    basis: z.enum(CONSENT_BASES),
    version: z.string().min(1).max(32),
});
// ---- Superadmin ----
// `search` matches email or display name; `limit` is capped server-side too.
export const listUsersSchema = z.object({
    search: z.string().trim().optional(),
    limit: z.coerce.number().int().positive().max(200).optional(),
});
// ---- File downloads ----
const documentFormat = z.enum(["pdf", "docx"]);
export const exportTestDocumentSchema = z.object({ testId: id, format: documentFormat, includeMemorandum: z.boolean().default(false) });
// File downloads arrive as query strings, so every value reaches zod as a
// string. z.coerce.boolean() is wrong here — it would read "false" as true.
const documentFlag = z
    .union([z.boolean(), z.enum(["true", "false"])])
    .transform((value) => value === true || value === "true");
/**
 * The eleven official languages, matching packages/ui/src/i18n/languages.ts. The
 * exporter needs this because a rendered document carries a little text of its
 * own -- the note under a node that has none yet -- and that should not be
 * English for a reader using the app in Tshivenda.
 */
export const DOCUMENT_LOCALES = ["en", "af", "nr", "nso", "st", "ss", "tn", "ts", "ve", "xh", "zu"];
export const exportStudyNotesSchema = z.object({
    subjectId: id,
    chapterId: id.optional(),
    format: documentFormat,
    /**
     * Subject-scoped exports append every chapter after the subject's own
     * content. Ignored when chapterId is set (a chapter has no chapters).
     */
    includeChapters: documentFlag.default(true),
    /** Language for the document text the exporter itself supplies. */
    locale: z.enum(DOCUMENT_LOCALES).default("en"),
});
export const COMMAND_NAMES = [
    "createSubject",
    "updateSubject",
    "archiveSubject",
    "activateSubject",
    "deleteSubject",
    "restoreSubject",
    "createChapter",
    "updateChapter",
    "archiveChapter",
    "activateChapter",
    "reorderChapters",
    "deleteChapter",
    "restoreChapter",
    "createCheckItem",
    "updateCheckItem",
    "reorderCheckItems",
    "deleteCheckItem",
    "createEvent",
    "updateEvent",
    "deleteEvent",
    "createMindMap",
    "createNode",
    "updateNode",
    "updateWriting",
    "updateMindMapWriting",
    "deleteNode",
    "createNodeRelationship",
    "updateNodeRelationship",
    "deleteNodeRelationship",
    "generateAITest",
    "updateAITest",
    "replaceAITestQuestions",
    "reorderAITests",
    "deleteAITest",
    "startTestAttempt",
    "saveQuestionResponse",
    "discardTestAttempt",
    "submitTestAttempt",
    "markFreeTextResponse",
    "updatePreferences",
    "acceptConsent",
    "grantCreditTopUp",
    "submitTranslationSuggestion",
    "submitTranslationReview",
    "setTranslationCampaign",
    "disableTranslationOverride",
];
export const QUERY_NAMES = [
    "getSubjectList",
    "getSubjectOverview",
    "getMindMap",
    "getAITest",
    "getAllAITests",
    "getTestAttempt",
    "getEventsByDateRange",
    "getUpcomingEvents",
    "getOpenCheckItems",
    "getAllCheckItems",
    "getRecentMindMaps",
    "getDeletedRecords",
    "getSession",
    "getCreditBalance",
    "getCreditPricing",
    "listCreditTransactions",
    "listTranslationWork",
    "getTranslationOverrides",
    "getTranslationModeration",
    "listUsers",
    "getInventory",
];
export const FILE_QUERY_NAMES = ["exportTestDocument", "exportStudyNotes"];
